/**
 * AdminCopfRolesPage — V9.6.1
 *
 * UI minimale pour la gestion du rôle COPF :
 *  - Liste les comptes COPF actifs + les préautorisations en attente
 *  - Permet d'attribuer le rôle à une ou plusieurs adresses e-mail
 *  - Permet de révoquer le rôle (compte existant ou préautorisation)
 *
 * Tous les changements sont tracés côté backend (audit_logs).
 * Réservé aux comptes `admin` strict — un COPF ne peut PAS y accéder.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, UserPlus, UserMinus, ArrowLeft, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { showError } from "@/lib/errors";

export default function AdminCopfRolesPage() {
  const { user } = useAuth();
  const isAdmin = user && (user.roles || []).includes("admin");

  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState([]);
  const [preauth, setPreauth] = useState([]);
  const [emailsInput, setEmailsInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState({});

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users/copf");
      setActive(r.data.active || []);
      setPreauth(r.data.preauthorized || []);
    } catch (e) {
      showError(e, { prefix: "Chargement impossible :" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) reload();
    else setLoading(false);
  }, [isAdmin]);

  const handleGrant = async () => {
    const emails = emailsInput
      .split(/[\s,;\n]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0 && e.includes("@"));
    if (emails.length === 0) {
      toast.error("Saisis au moins un e-mail valide.");
      return;
    }
    setGrantBusy(true);
    try {
      const r = await api.post("/admin/users/copf/grant", {
        emails,
        reason: reasonInput.trim() || undefined,
      });
      const { granted = [], skipped = [] } = r.data;
      const applied = granted.filter((g) => g.status === "applied").length;
      const preauthed = granted.filter((g) => g.status === "preauthorized").length;
      const skips = skipped.length;
      toast.success(
        `${granted.length} action(s) — appliqué : ${applied}, préautorisé : ${preauthed}, ignoré : ${skips}`,
      );
      setEmailsInput("");
      setReasonInput("");
      reload();
    } catch (e) {
      showError(e, { prefix: "Attribution échouée :" });
    } finally {
      setGrantBusy(false);
    }
  };

  const handleRevoke = async (target) => {
    const key = target.user_id || target.email;
    if (!confirm(`Révoquer le rôle COPF de ${target.email || target.user_id} ?`)) return;
    setRevokeBusy((s) => ({ ...s, [key]: true }));
    try {
      await api.post("/admin/users/copf/revoke", {
        email: target.email,
        user_id: target.user_id,
        reason: "Révocation depuis UI admin",
      });
      toast.success(`Rôle COPF révoqué pour ${target.email || target.user_id}.`);
      reload();
    } catch (e) {
      showError(e, { prefix: "Révocation échouée :" });
    } finally {
      setRevokeBusy((s) => ({ ...s, [key]: false }));
    }
  };

  if (!user) {
    return (
      <div className="p-8">
        <Card className="p-6 text-center">Connecte-toi pour accéder à cette page.</Card>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Card className="p-6">
          <AlertCircle className="h-6 w-6 text-[hsl(var(--aito-energy))]" />
          <h2 className="font-display text-xl font-bold mt-2">Accès réservé aux administrateurs</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Seul un compte avec le rôle <code>admin</code> peut gérer les rôles COPF
            (séparation des pouvoirs). Les comptes COPF n'ont pas accès à cette page.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-copf-roles-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        data-testid="copf-back-admin"
      >
        <ArrowLeft className="h-3 w-3" /> Retour Admin
      </Link>
      <h1 className="font-display text-3xl font-bold mt-2 flex items-center gap-2">
        <ShieldCheck className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" />
        Gestion du rôle COPF
      </h1>
      <p className="text-sm text-muted-foreground mt-1">
        Attribuer / révoquer le rôle COPF pour les membres du Comité Olympique Polynésien.
        Les comptes existants sont mis à jour immédiatement. Les e-mails inconnus sont
        préautorisés : le rôle sera appliqué automatiquement à la première connexion.
      </p>

      <Separator className="my-6" />

      {/* --------- BLOC ATTRIBUTION --------- */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
          <h2 className="font-display text-lg font-bold">Attribuer le rôle COPF</h2>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium" htmlFor="copf-emails">
              E-mails (un par ligne, ou séparés par virgule)
            </label>
            <Textarea
              id="copf-emails"
              data-testid="copf-emails-input"
              rows={3}
              placeholder={"president@copf.pf\nsecretaire@copf.pf"}
              value={emailsInput}
              onChange={(e) => setEmailsInput(e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium" htmlFor="copf-reason">
              Motif (optionnel, tracé dans l'audit)
            </label>
            <Input
              id="copf-reason"
              data-testid="copf-reason-input"
              placeholder="ex : nomination AG 2026"
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
            />
          </div>
          <div>
            <Button
              onClick={handleGrant}
              disabled={grantBusy || !emailsInput.trim()}
              data-testid="copf-grant-button"
              className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
            >
              {grantBusy ? "Attribution…" : "Attribuer le rôle COPF"}
            </Button>
          </div>
        </div>
      </Card>

      {/* --------- BLOC COMPTES ACTIFS --------- */}
      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <h2 className="font-display text-lg font-bold">Comptes COPF actifs</h2>
          </div>
          <Badge variant="secondary" data-testid="copf-active-count">
            {active.length}
          </Badge>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : active.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun compte COPF actif pour l'instant.</p>
        ) : (
          <ul className="divide-y" data-testid="copf-active-list">
            {active.map((u) => (
              <li key={u.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name || u.email}</div>
                  <div className="text-xs text-muted-foreground truncate font-mono">{u.email}</div>
                  {u.copf_granted_at && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Promu le {new Date(u.copf_granted_at).toLocaleDateString("fr-FR")}
                      {u.copf_granted_by_email && ` par ${u.copf_granted_by_email}`}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRevoke({ user_id: u.id, email: u.email })}
                  disabled={revokeBusy[u.id]}
                  data-testid={`copf-revoke-${u.id}`}
                >
                  <UserMinus className="h-3.5 w-3.5 mr-1" />
                  Révoquer
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* --------- BLOC PRÉAUTORISATIONS EN ATTENTE --------- */}
      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            <h2 className="font-display text-lg font-bold">Préautorisations en attente</h2>
          </div>
          <Badge variant="secondary" data-testid="copf-preauth-count">
            {preauth.length}
          </Badge>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : preauth.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune préautorisation en attente.</p>
        ) : (
          <ul className="divide-y" data-testid="copf-preauth-list">
            {preauth.map((p) => (
              <li key={p.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-sm truncate">{p.email}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Préautorisé le {new Date(p.granted_at).toLocaleDateString("fr-FR")}
                    {p.granted_by_email && ` par ${p.granted_by_email}`}
                    {p.reason && ` · « ${p.reason} »`}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRevoke({ email: p.email })}
                  disabled={revokeBusy[p.email]}
                  data-testid={`copf-revoke-preauth-${p.id}`}
                >
                  <UserMinus className="h-3.5 w-3.5 mr-1" />
                  Annuler
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
